"use client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-client";
import { isDoctorSpecialty } from "@/lib/doctor-profile";
function keepPreviousData(prev) {
    return prev;
}
export class DoctorDataError extends Error {
    constructor(status) {
        super("Failed to fetch doctor data");
        this.name = "DoctorDataError";
        this.status = status;
    }
}
function readDoctorField(data, key) {
    var _a;
    if (!data || typeof data !== "object")
        return undefined;
    const root = data;
    const nested = root.doctor && typeof root.doctor === "object"
        ? root.doctor
        : null;
    const raw = (_a = nested === null || nested === void 0 ? void 0 : nested[key]) !== null && _a !== void 0 ? _a : root[key];
    return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}
function readDoctorBool(data, key) {
    var _a;
    if (!data || typeof data !== "object")
        return undefined;
    const root = data;
    const nested = root.doctor && typeof root.doctor === "object"
        ? root.doctor
        : null;
    const raw = (_a = nested === null || nested === void 0 ? void 0 : nested[key]) !== null && _a !== void 0 ? _a : root[key];
    return typeof raw === "boolean" ? raw : undefined;
}
function parseLegacyProfile(description) {
    if (!(description === null || description === void 0 ? void 0 : description.trim()))
        return {};
    const parts = description
        .split(" · ")
        .map((part) => part.trim())
        .filter(Boolean);
    const out = {};
    if (parts[0] && isDoctorSpecialty(parts[0])) {
        out.specialty = parts[0];
    }
    if (parts[1] && /^[A-Za-z]{2}$/.test(parts[1])) {
        out.country = parts[1].toUpperCase();
    }
    return out;
}
async function fetchDoctorData(token) {
    var _a, _b, _c, _d, _e, _f;
    const res = await apiFetch("/api/doctors/me", { token });
    if (!res.ok)
        throw new DoctorDataError(res.status);
    const data = await res.json();
    const doctor = ((data === null || data === void 0 ? void 0 : data.doctor) || data);
    const description = readDoctorField(data, "description");
    const legacy = parseLegacyProfile(description);
    const specialty = (_a = readDoctorField(data, "specialty")) !== null && _a !== void 0 ? _a : legacy.specialty;
    const country = (_b = readDoctorField(data, "country")) !== null && _b !== void 0 ? _b : legacy.country;
    return {
        _rawResponse: data,
        email: readDoctorField(data, "email"),
        name: readDoctorField(data, "name"),
        surname: readDoctorField(data, "surname"),
        description,
        specialty,
        country,
        interface_language: readDoctorField(data, "interface_language"),
        theme: readDoctorField(data, "theme"),
        mcpAccessEnabled: (_c = readDoctorBool(data, "mcp_access_enabled")) !== null && _c !== void 0 ? _c : false,
        doctor: doctor
            ? Object.assign(Object.assign({}, doctor), { specialty: (_d = specialty !== null && specialty !== void 0 ? specialty : doctor.specialty) !== null && _d !== void 0 ? _d : null, country: country !== null && country !== void 0 ? country : doctor.country }) : undefined,
        doctorToken: (typeof (data === null || data === void 0 ? void 0 : data.token) === "string" && data.token) ||
            (typeof (doctor === null || doctor === void 0 ? void 0 : doctor.token) === "string" && doctor.token) ||
            undefined,
        doctorUserId: (_f = (_e = doctor === null || doctor === void 0 ? void 0 : doctor.chat) === null || _e === void 0 ? void 0 : _e.getstream_user_id) !== null && _f !== void 0 ? _f : undefined,
    };
}
export function useDoctorData(token) {
    var _a;
    const { token: authToken } = useAuth();
    const effectiveToken = (_a = token !== null && token !== void 0 ? token : authToken) !== null && _a !== void 0 ? _a : null;
    return useQuery({
        queryKey: ["doctor", "me", effectiveToken],
        queryFn: () => fetchDoctorData(effectiveToken),
        enabled: !!effectiveToken,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        placeholderData: keepPreviousData,
    });
}
